// Steps 1 and 2 of the campaign builder: the production and the channel.

import { ChannelPill } from '@/components/primitives';
import { MetaDeliveryPolicy, MetaDeliveryPolicyLoading } from '@/components/meta-delivery-policy';
import { RequiredField } from '@/components/required-field';
import { LockBanner, LockHint, LockedField } from '@/components/lock-primitives';
import { Section } from '@/components/wizard-section';
import { CHANNEL_FORMATS } from './creative-config';
import { fmtDay } from '@/lib/datetime';
import { VENUE_UNKNOWN } from '@/lib/manage-screen-adapters';
import { ProductionPicker } from './create-production-picker';
import { useChannelBenchmarks } from '@/api/manage-hooks';
import { performanceMetricCell } from '@/components/performance-metric-cell';
import type { DirectPerformanceMetrics } from '@/lib/performance-metrics';
import type { CreateFlowModel } from './create-flow-model';

export function ProductionStep({ model }: { model: CreateFlowModel }) {
  const { wasLive, production, setShow, required, open, setOpen, toggle,
  } = model;
  return (
    <Section n="1" title="Production" summary={production ? `${production.title} · ${production.venue ?? VENUE_UNKNOWN}` : 'Select the production this campaign drives ticket sales for.'} open={open.production} done={!!production} onToggle={()=>toggle('production')} locked={wasLive}>
      <div className="flex between items-center mb-3">
        <div className="muted" style={{fontSize:11.5}}>Production anchors campaign reporting, brand kit, landing pages, and historical conversion priors. Audience filters remain explicit.</div>
        {production && <button className="btn btn-sm" onClick={()=>{setShow(null);}}>Change production</button>}
      </div>
      <RequiredField
        fieldKey="campaign-production"
        message={required.messages['campaign-production']}
        attempt={required.attempt}
        surface
      >
        <ProductionPicker
          selected={production}
          onSelect={(s)=>{
            required.clear('campaign-production');
            setShow(s);
            setOpen({...open, production: false, ch: true});
          }}
        />
      </RequiredField>
    </Section>
  );
}

/** One channel tile's Reports figures: CPM, CPA and ROAS, or why there are none. */
function ChannelTileStats({ metrics, pending, failed }: { metrics: DirectPerformanceMetrics | undefined; pending: boolean; failed: boolean }) {
  if (failed) return <div className="ch-tile-stats"><span className="muted">Reports unavailable</span></div>;
  if (pending) return <div className="ch-tile-stats"><span className="muted">Loading Reports…</span></div>;
  if (!metrics) return <div className="ch-tile-stats"><span className="muted">No history yet</span></div>;
  const stat = (metric: 'cpm' | 'cpa' | 'roas', label: string) => (
    <span key={metric}><b>{performanceMetricCell(metrics, metric)}</b> {label}</span>
  );
  return (
    <div className="ch-tile-stats" title="Direct sales in Reports → Performance, last 180 days">
      {stat('cpm', 'CPM')}
      {stat('cpa', 'CPA')}
      {stat('roas', 'ROAS')}
      <span className="muted">last 180 days in Reports</span>
    </div>
  );
}

export function ChannelStep({ model }: { model: CreateFlowModel }) {
  const { wasLive, channel, setChannel, metaContractQuery, metaContract, startDate, production,
    required, open, setOpen, toggle, handleDuplicate,
  } = model;
  const benchmarks = useChannelBenchmarks(production?.id ?? null);
  return (
    <Section n="2" title="Channel" summary={channel ? `${{meta:'Meta',ctv:'CTV',dsp:'Display'}[channel]} · ${CHANNEL_FORMATS[channel].blurb}` : 'One channel per campaign. Each has tuned defaults.'} open={open.ch} done={!!channel} onToggle={()=>toggle('ch')} locked={wasLive} lockNotice={
      <LockBanner className="locked-banner--section" onDuplicate={() => { void handleDuplicate(); }}>
        Channel can't be changed after the publishing handoff — attribution and pacing models are channel-specific. Duplicate to test another channel against the same audience.
      </LockBanner>
    }>
      {wasLive ? (
        <>
          <LockedField
            label="Channel"
            value={channel ? CHANNEL_FORMATS[channel].name : '—'}
            sublabel={channel ? CHANNEL_FORMATS[channel].blurb : undefined}
            reason={`Locked after publishing handoff · ${fmtDay(startDate)}.`}
          />
        </>
      ) : (
      <RequiredField
        fieldKey="campaign-channel"
        message={required.messages['campaign-channel']}
        attempt={required.attempt}
        className="channel-tiles"
        surface
      >
        {(['meta','ctv','dsp'] as const).map(ch => {
          const cfg = CHANNEL_FORMATS[ch];
          return (
            <div role="button" tabIndex={0} aria-pressed={channel === ch} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();e.currentTarget.click();}}} key={ch} className={`ch-tile ${channel === ch ? 'selected' : ''}`} onClick={()=>{
              required.clear('campaign-channel');
              setChannel(ch);
              setOpen({...open, ch: false, aud: true});
            }}>
              <div className="ch-tile-hd"><div className="ch-tile-name">{cfg.name}</div><ChannelPill channel={ch}/></div>
              <div className="muted" style={{fontSize:11.5,lineHeight:1.4}}>{cfg.blurb}</div>
              <div className="ch-tile-formats">{cfg.formats.slice(0,4).map(f => <span key={f.name}>{f.name}</span>)}</div>
              <ChannelTileStats metrics={benchmarks.benchmarks.get(ch)} pending={benchmarks.isPending} failed={benchmarks.isError}/>
            </div>
          );
        })}
      </RequiredField>
      )}
      {!wasLive && (
        <div style={{marginTop:10,fontSize:11.5,color:'var(--ink-2)',display:'flex',alignItems:'center',gap:6}}>
          <LockHint/> <span>Channel becomes permanent once the campaign is marked ready to publish.</span>
        </div>
      )}
      {channel === 'meta' && metaContractQuery.isPending && <MetaDeliveryPolicyLoading />}
      {channel === 'meta' && metaContractQuery.isError && (
        <div className="facet" role="alert" style={{marginTop:12,borderColor:'var(--bad)',color:'var(--bad)'}}>
          Meta delivery settings could not be loaded.
        </div>
      )}
      <MetaDeliveryPolicy contract={metaContract} scopes={['campaign']} />
    </Section>
  );
}
